import {
  BlockNoteSchema,
  createHeadingBlockSpec,
  defaultBlockSpecs,
  defaultInlineContentSpecs,
} from '@blocknote/core';
import * as React from 'react';
import type * as SelectPrimitive from '@radix-ui/react-select';

import {
  dateBlockSpec,
  injectSelectComponents,
  dropdownBlockSpec,
  horizontalRuleBlockSpec,
  createHorizontalRuleBlockSpec,
  phraseBlockSpec,
} from './custom-blocks';
import { tokenInlineSpec } from './custom-inline';

interface BlockNoteSchemaOptions {
  /**
   * Optional getter used by custom blocks that need to know whether the
   * surrounding editor is editable. Preferred over reading
   * `editor.isEditable` directly from within a custom block's React render,
   * because that value does not always update reliably in response to
   * `BlockNoteView`'s `editable` prop (see TypeCellOS/BlockNote#1477).
   */
  getIsEditable?: () => boolean;
}

/**
 * Pure function that creates the extended BlockNote editor schema.
 * Includes custom blocks (dropdown, date, horizontalRule) and inline content
 * (token).
 *
 * Note: Select components must be injected via `injectSelectComponents()` before
 * calling this function, as dropdown blocks depend on them.
 */
function createSchema(options: BlockNoteSchemaOptions = {}) {
  // Validate that block specs are defined
  if (!dropdownBlockSpec) {
    throw new Error('dropdownBlockSpec is not defined');
  }
  if (!dateBlockSpec) {
    throw new Error('dateBlockSpec is not defined');
  }
  if (!horizontalRuleBlockSpec) {
    throw new Error('horizontalRuleBlockSpec is not defined');
  }
  if (!phraseBlockSpec) {
    throw new Error('phraseBlockSpec is not defined');
  }

  const horizontalRuleSpec = options.getIsEditable
    ? createHorizontalRuleBlockSpec({ getIsEditable: options.getIsEditable })
    : horizontalRuleBlockSpec;

  // Build blockSpecs as a typed object literal
  // Spread defaultBlockSpecs first, then override/add custom specs
  const blockSpecs = {
    ...defaultBlockSpecs,
    heading: createHeadingBlockSpec({
      levels: [1, 2, 3, 4, 5, 6],
    }),
    dropdown: dropdownBlockSpec,
    date: dateBlockSpec,
    horizontalRule: horizontalRuleSpec,
    phrase: phraseBlockSpec,
  };

  const inlineSpecs = {
    ...defaultInlineContentSpecs,
    token: tokenInlineSpec,
  };

  try {
    const schema = BlockNoteSchema.create({
      blockSpecs,
      inlineContentSpecs: inlineSpecs,
    });

    return schema;
  } catch (error) {
    // Clean error surface: throw concise error with context
    // Include cause if supported, otherwise include message in error
    const errorMessage = error instanceof Error ? error.message : String(error);
    const schemaError = new Error(
      `Failed to create BlockNote schema: ${errorMessage}`,
    );
    if (error instanceof Error) {
      (schemaError as Error & { cause?: Error }).cause = error;
    }
    throw schemaError;
  }
}

/**
 * Creates the extended BlockNote editor schema that includes the dropdown block.
 * Backward-compatible wrapper that handles Select component injection.
 *
 * Select components must be injected before calling this function to break circular dependency:
 * - web-common needs Select from ui
 * - ui needs schema from web-common
 * Solution: Inject Select components in ui (where they're available) before creating the schema
 */
export function createSchemaWithDropdown(
  selectComponents: {
    Select: typeof SelectPrimitive.Root;
    SelectContent: React.ComponentType<
      React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
    >;
    SelectItem: React.ComponentType<
      React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
    >;
    SelectTrigger: React.ComponentType<
      React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
    >;
    SelectValue: typeof SelectPrimitive.Value;
  },
  options: BlockNoteSchemaOptions = {},
) {
  // Inject Select components into dropdown-block before creating the schema
  injectSelectComponents(selectComponents);

  // Create schema (pure function, no side effects)
  return createSchema(options);
}
